import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, filter, switchMap, finalize } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { PaymentGatewayModel, Payment_Gateway_Enum } from 'src/app/models/portal';
import { selectWebsitePaymentGatewayByEnum } from 'src/app/shared/store/_features/website/payments/website-payments.selector';
import { isWebsiteLoaded } from 'src/app/shared/store/_features/website/website.selector';
import { websitePaymentGatewaysActions } from 'src/app/shared/store/_features/website/payments/website-payments.actions';

// Define integration steps
enum IntegrationStep {
  FORM = 1,
  SUCCESS = 2
}

@Component({
  selector: 'app-payment-integrate',
  templateUrl: './integrate.component.html',
  styleUrls: ['./integrate.component.css'],
})
export class IntegratePortalPaymentsComponent implements OnInit, OnDestroy {
  @Input() gateway: Payment_Gateway_Enum;

  currentPaymentGateway: PaymentGatewayModel | null = null;
  isLoading = true;
  hasError = false;

  // Progress tracking
  currentStep: IntegrationStep = IntegrationStep.FORM;
  IntegrationStep = IntegrationStep; // Expose enum to template

  // Success state
  isProcessing = false;
  showSuccessAnimation = false;

  private destroy$ = new Subject<void>();

  private readonly DOCS_URLS: Record<string, string> = {
    [Payment_Gateway_Enum.stripe]: 'https://stripe.com/docs/connect',
    [Payment_Gateway_Enum.paypal]: 'https://developer.paypal.com/docs/platforms/',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private title: Title
  ) { }

  ngOnInit(): void {
    this.initializeGateway();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeGateway(): void {
    this.isLoading = true;
    this.hasError = false;

    this.store.select(isWebsiteLoaded).pipe(
      filter(loaded => !!loaded),
      switchMap(() => {
        if (!this.gateway) {
          const slug = this.route.snapshot.params['slug'];
          this.gateway = slug as Payment_Gateway_Enum;
        }
        return this.store.select(selectWebsitePaymentGatewayByEnum(this.gateway));
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (gateway) => {
        if (!gateway) {
          this.handleGatewayNotFound();
          return;
        }

        this.currentPaymentGateway = gateway;
        this.updatePageTitle();
        this.isLoading = false;

        if (gateway.integrated) {
          this.navigateToView(gateway.id);
        }
      },
      error: (err) => {
        console.error('Error loading gateway configuration', err);
        this.handleLoadError();
      }
    });
  }

  private updatePageTitle(): void {
    if (this.currentPaymentGateway) {
      this.title.setTitle(`Connect ${this.currentPaymentGateway.name} | Integration Settings`);
    }
  }

  getDocumentationUrl(): string {
    if (!this.gateway) return '#';
    return this.DOCS_URLS[this.gateway] || 'https://yoursupportpage.com/payments';
  }

  private handleGatewayNotFound(): void {
    this.hasError = true;
    this.isLoading = false;
  }

  private handleLoadError(): void {
    this.hasError = true;
    this.isLoading = false;
  }

  onIntegrationSuccess(methodSlug: Payment_Gateway_Enum): void {
    // Show success step

    this.currentStep = IntegrationStep.SUCCESS;
    this.showSuccessAnimation = true;


    // Navigate after delay to show success state
    setTimeout(() => {

      this.store.dispatch(websitePaymentGatewaysActions.load());
    }, 2000);
  }

  private navigateToView(methodSlug: Payment_Gateway_Enum): void {
    this.router.navigate(['../../', methodSlug.toLowerCase(), 'view'], {
      relativeTo: this.route
    });
  }

  navigateBack(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  // Helper method to check if step is complete
  isStepComplete(step: IntegrationStep): boolean {
    return this.currentStep > step;
  }

  // Helper method to check if step is current
  isStepCurrent(step: IntegrationStep): boolean {
    return this.currentStep === step;
  }
}